import { useCallback, useEffect, useRef, useState } from 'react';

interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

/**
 * Run an async loader and expose its state.
 *
 * Two details that matter on a dashboard: a result that arrives after the component has
 * unmounted is discarded, and a slow response that is superseded by a newer one is
 * ignored rather than overwriting fresher data.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const mounted = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);

    loader()
      .then((result) => {
        if (!mounted.current || current !== generation.current) return;
        setData(result);
      })
      .catch((err) => {
        if (!mounted.current || current !== generation.current) return;
        setError(err);
      })
      .finally(() => {
        if (!mounted.current || current !== generation.current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}

/** Re-run a loader on an interval — used by the live dashboard tiles. */
export function usePolling<T>(loader: () => Promise<T>, intervalMs: number, deps: unknown[] = []): AsyncState<T> {
  const state = useAsync(loader, deps);
  const reload = state.reload;

  useEffect(() => {
    if (intervalMs <= 0) return;
    const timer = window.setInterval(() => {
      // Don't poll a hidden tab; it wastes requests and can pile up on wake.
      if (document.visibilityState === 'visible') reload();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, reload]);

  return state;
}
